// Fiscal-close date helpers — FY-DERIVED, never hardcoded to FY26/2026.
//
// DCVFD/PWC fiscal year runs Jul 1 – Jun 30; the FY label is the year it ENDS.
// For the PO close worklist we need two deadlines for the CURRENT fiscal year:
//   • fiscal close                       — June 30
//   • year-end P-Card allocation deadline — June 24 (a fixed PWC calendar date,
//     ahead of close; NOT tied to any per-PO payment-medium flag)
// Both derive from the clock so they roll forward automatically each year.

/** The calendar year in which the current fiscal year ends (e.g. 2026 for FY26). */
export function fiscalYearEndCalendarYear(now: Date = new Date()): number {
  const month = now.getUTCMonth() + 1; // 1–12
  const year = now.getUTCFullYear();
  // Jul–Dec ⇒ FY ends next calendar year; Jan–Jun ⇒ ends this calendar year.
  return month >= 7 ? year + 1 : year;
}

/**
 * The current fiscal-year SHORT LABEL, e.g. 'FY26' (the year the FY ends).
 * Mirrors the worker's `currentFiscalYear()` (finance-po-api.ts) so the
 * frontend can derive the same label from the clock without importing worker
 * code.
 *
 * THIS IS THE SINGLE SOURCE OF TRUTH FOR "THE FISCAL YEAR WE ARE IN RIGHT NOW."
 * Derive every page's *current-FY label* from this (the clock) — NEVER from the
 * data's `as_of` date. Keying the label off the data freezes it at the data's FY
 * (e.g. stuck on 'FY26' for all of FY27 until the county loads FY27), which reads
 * as "prior-year data is the current position." Use `maxFiscalYear`/the data's
 * `as_of` only to decide whether what's loaded is the CURRENT vs a PRIOR year
 * (see `fyContext` / `fyContextFromAsOf`), not to compute the label.
 */
export function currentFiscalYear(now: Date = new Date()): string {
  return `FY${String(fiscalYearEndCalendarYear(now)).slice(2)}`;
}

/**
 * The levy COA fund string for a fiscal year. PWC's OMB consolidates the VFD
 * funds from **2101 → 2119 effective FY27 (7/1/2026)**; only the fund segment
 * changes — dept/activity 656500 is unchanged. Route every displayed fund label
 * through this so the dashboard, badges, and board package flip automatically at
 * the rollover instead of showing a stale 2101. Accepts 'FY26' / 'FY27' / '26'.
 */
export function fundForFy(fy: string | null | undefined): string {
  const m = /(\d{2})$/.exec(String(fy ?? ""));
  const n = m ? Number(m[1]) : fiscalYearEndCalendarYear() % 100;
  return n >= 27 ? "2119-65-6560" : "2101-65-6560";
}

/**
 * The prior fiscal-year short label relative to `fy` (e.g. 'FY25' for 'FY26').
 * Accepts an 'FYxx' label and returns the label one year earlier; returns the
 * input unchanged if it isn't a recognizable 'FYxx' string.
 */
export function priorFiscalYear(fy: string): string {
  const m = /^FY(\d{2})$/.exec(fy);
  if (!m) return fy;
  return `FY${String(Number(m[1]) - 1).padStart(2, "0")}`;
}

/**
 * The next fiscal-year short label relative to `fy` (e.g. 'FY27' for 'FY26').
 * This is the year being BUDGETED while `fy` is the current position year.
 * Returns the input unchanged if it isn't a recognizable 'FYxx' string.
 */
export function nextFiscalYear(fy: string): string {
  const m = /^FY(\d{2})$/.exec(fy);
  if (!m) return fy;
  return `FY${String(Number(m[1]) + 1).padStart(2, "0")}`;
}

/**
 * The newest (max) 'FYxx' label present in a list of fiscal-year strings, or
 * `null` if none parse.
 *
 * This is a DATA-FY helper: it tells you the newest FY the API actually
 * returned. It is NOT a source for "the current fiscal year" — that is always
 * the clock (`currentFiscalYear`). Use this to (a) anchor a YoY comparison (pass
 * through `priorFiscalYear`) or (b) feed `fyContext` so a page can tell whether
 * its loaded data is the current or a prior year and label/banner accordingly.
 */
export function maxFiscalYear(
  values: ReadonlyArray<string | null | undefined>,
): string | null {
  let best: number | null = null;
  for (const v of values) {
    const m = v && /^FY(\d{2})$/.exec(v);
    if (!m) continue;
    const n = Number(m[1]);
    if (best === null || n > best) best = n;
  }
  return best === null ? null : `FY${String(best).padStart(2, "0")}`;
}

/** Numeric part of an 'FYxx' label (e.g. 'FY26' → 26), or null if unparseable. */
function fyNum(fy: string | null | undefined): number | null {
  const m = /^FY(\d{2})$/.exec(String(fy ?? ""));
  return m ? Number(m[1]) : null;
}

/**
 * The transition context every finance surface should key its FY framing off.
 * Separates "the FY we are in NOW" (clock) from "the FY the loaded data is for"
 * so a page can label the current FY honestly AND flag when what it's showing is
 * actually a prior (or, defensively, a future) year's data.
 */
export interface FyContext {
  /** Clock — the fiscal year we are in right now. Use for the page's FY label. */
  currentFy: string;
  /** The FY the loaded data represents (from `as_of` / max `fiscal_year`), or null. */
  dataFy: string | null;
  /** dataFy is an EARLIER FY than currentFy → show a prior-year banner/label. */
  isPriorYear: boolean;
  /** dataFy is a LATER FY than currentFy (defensive; shouldn't normally happen). */
  isFuture: boolean;
  /** dataFy === currentFy → the loaded data is genuinely the current position. */
  hasCurrentData: boolean;
  /** Fund string of the NUMBERS ACTUALLY SHOWN (keyed to dataFy). */
  fund: string;
  /** Fund string of the current (clock) FY — for "FY27 (fund 2119) not loaded" copy. */
  currentFund: string;
}

/**
 * Build an {@link FyContext} from the FY the data represents. `dataFy` should be
 * the data's own FY (e.g. `maxFiscalYear(rows.map(r => r.fiscal_year))`); pass
 * `null` when nothing is loaded.
 */
export function fyContext(
  dataFy: string | null | undefined,
  now: Date = new Date(),
): FyContext {
  const currentFy = currentFiscalYear(now);
  const cn = fyNum(currentFy);
  const dn = fyNum(dataFy);
  return {
    currentFy,
    dataFy: dataFy ?? null,
    isPriorYear: dn != null && cn != null && dn < cn,
    isFuture: dn != null && cn != null && dn > cn,
    hasCurrentData: dataFy != null && dataFy === currentFy,
    fund: fundForFy(dataFy ?? currentFy),
    currentFund: fundForFy(currentFy),
  };
}

/**
 * Build an {@link FyContext} from a data `as_of` date string ('YYYY-MM-DD') —
 * the levy surfaces' only FY discriminator. Replaces the buggy
 * `as_of ? currentFiscalYear(new Date(as_of)) : currentFiscalYear()` pattern,
 * which mislabeled prior-year data as the current FY.
 */
export function fyContextFromAsOf(
  asOf: string | null | undefined,
  now: Date = new Date(),
): FyContext {
  const dataFy = asOf ? currentFiscalYear(new Date(`${asOf}T00:00:00Z`)) : null;
  return fyContext(dataFy, now);
}

/** True when `dataFy` is an earlier fiscal year than the one we are in now. */
export function isPriorYearData(
  dataFy: string | null | undefined,
  now: Date = new Date(),
): boolean {
  return fyContext(dataFy, now).isPriorYear;
}

/** Whole days from `now` until `target` (UTC, calendar-day granularity). */
export function daysUntil(target: Date, now: Date = new Date()): number {
  const a = Date.UTC(
    target.getUTCFullYear(),
    target.getUTCMonth(),
    target.getUTCDate(),
  );
  const b = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((a - b) / 86_400_000);
}

export interface FiscalCloseDeadlines {
  /** ISO 'YYYY-MM-DD' of fiscal close (June 30 of the FY-end year). */
  fiscalCloseDate: string;
  /** ISO 'YYYY-MM-DD' of the year-end P-Card allocation deadline (June 24 — a
   *  fixed PWC calendar date, ahead of close; not a per-PO flag). */
  pcardDate: string;
  /** Whole days until fiscal close (negative once past). */
  daysToClose: number;
  /** Whole days until the year-end P-Card allocation deadline (negative once past). */
  daysToPcard: number;
  /** The FY-end calendar year (e.g. 2026). */
  fyEndYear: number;
}

/**
 * Compute the current-FY fiscal-close + year-end P-Card allocation deadlines
 * and the countdown to each.
 * Pure + injectable `now` for testing.
 */
export function fiscalCloseDeadlines(
  now: Date = new Date(),
): FiscalCloseDeadlines {
  const fyEndYear = fiscalYearEndCalendarYear(now);
  const close = new Date(Date.UTC(fyEndYear, 5, 30)); // June = month index 5
  const pcard = new Date(Date.UTC(fyEndYear, 5, 24));
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return {
    fiscalCloseDate: iso(close),
    pcardDate: iso(pcard),
    daysToClose: daysUntil(close, now),
    daysToPcard: daysUntil(pcard, now),
    fyEndYear,
  };
}
